import {
  layoutDistributability,
  PLATFORM_LABELS,
  type LayoutDistributability,
  type SourceLayout,
} from '@shopx-distribution/shared';
import { Check } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useCreateDistribution, useDistributions, useSource, useTargets } from '@/api/hooks.js';
import { DistributionList } from '@/components/DistributionList.js';
import { PlatformIcon } from '@/components/PlatformIcon.js';
import { Button, Card, EmptyState, ErrorNotice, Loading, SectionHeading } from '@/components/ui.js';
import { cn, formatAccountLine, formatDimensions } from '@/lib/utils.js';

/**
 * Choose which layouts go where.
 *
 * Selecting several targets creates one distribution per target rather than one
 * shared run, so a rejection by one ad account leaves the others untouched.
 *
 * Print layouts are reduced to a count in the section description rather than
 * rendered: they can never be sent, and giving them cards pushed the layouts
 * that can be sent below the fold. The count stays so that someone who authored
 * one can see it was recognised rather than lost.
 */
export function DistributePage() {
  const { sourceId = null } = useParams();
  const navigate = useNavigate();

  const source = useSource(sourceId);
  const targets = useTargets();
  const previous = useDistributions(sourceId ?? undefined);
  const createDistribution = useCreateDistribution();

  const [selectedLayouts, setSelectedLayouts] = useState<string[]>([]);
  const [selectedTargets, setSelectedTargets] = useState<string[]>([]);

  const toggle = (list: string[], id: string) =>
    list.includes(id) ? list.filter((value) => value !== id) : [...list, id];

  const submit = async () => {
    if (!sourceId) return;
    const result = await createDistribution.mutateAsync({
      sourceId,
      layoutIds: selectedLayouts,
      targetIds: selectedTargets,
    });
    const first = result.distributions[0];
    navigate(first ? `/experience/distributions/${first.id}` : '/experience');
  };

  if (source.isLoading) return <Loading label="Loading project…" />;
  if (source.error) return <ErrorNotice error={source.error} />;
  if (!source.data) return null;

  const layouts = source.data.source.layouts.map((layout) => ({
    layout,
    state: layoutDistributability(layout),
  }));
  // Print is split out rather than mixed in: every card in that group is
  // blocked for the same reason, so it is stated once above the group instead
  // of repeated on each card.
  const digital = layouts.filter(({ layout }) => layout.outputIntent !== 'print');
  const print = layouts.filter(({ layout }) => layout.outputIntent === 'print');
  const selectableIds = layouts
    .filter(({ state }) => state.status !== 'blocked')
    .map(({ layout }) => layout.id);
  const allSelectableSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedLayouts.includes(id));
  const warnedSelection = layouts
    .filter(({ layout, state }) => state.status === 'warned' && selectedLayouts.includes(layout.id))
    .map(({ layout }) => layout);
  const canSubmit =
    selectedLayouts.length > 0 && selectedTargets.length > 0 && !createDistribution.isPending;

  return (
    <div className="space-y-8">
      <div>
        <Link to="/experience" className="text-sm text-muted hover:underline">
          ← All projects
        </Link>
        <h2 className="mt-2 text-xl font-semibold">{source.data.source.name}</h2>
      </div>

      <section>
        <SectionHeading
          title={`${digital.length} digital layout${digital.length === 1 ? '' : 's'}`}
          description={
            'Each layout is sent as the preview image GraFx renders for it.' +
            (print.length > 0
              ? ` ${print.length} print layout${print.length === 1 ? ' is' : 's are'} hidden — ad channels only take digital.`
              : '')
          }
          action={
            <Button
              variant="secondary"
              onClick={() => setSelectedLayouts(allSelectableSelected ? [] : selectableIds)}
            >
              {allSelectableSelected ? 'Clear all' : 'Select all'}
            </Button>
          }
        />

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6">
          {digital.map(({ layout, state }) => (
            <LayoutCard
              key={layout.id}
              layout={layout}
              state={state}
              reason={state.status === 'ok' ? null : state.reason}
              selected={selectedLayouts.includes(layout.id)}
              onToggle={() => setSelectedLayouts(toggle(selectedLayouts, layout.id))}
            />
          ))}
        </div>
      </section>

      <section>
        <SectionHeading
          title="Channels"
          description="Ad accounts and DAM systems configured for this tenant."
        />

        {targets.data && targets.data.targets.length === 0 ? (
          <EmptyState
            title="No channels configured"
            description="An administrator connects ad platforms and DAM systems in the Admin Console."
          />
        ) : null}

        <div className="grid gap-2">
          {targets.data?.targets.map((target) => {
            const selected = selectedTargets.includes(target.id);
            return (
              <button
                key={target.id}
                type="button"
                role="checkbox"
                aria-checked={selected}
                onClick={() => setSelectedTargets(toggle(selectedTargets, target.id))}
                className={cn(
                  'flex items-center justify-between gap-4 rounded-brand border bg-surface px-4 py-3 text-left transition',
                  selected ? 'border-brand' : 'border-line hover:bg-hover',
                )}
              >
                <span className="flex min-w-0 items-center gap-3">
                  <PlatformIcon platform={target.platform} />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">
                      {PLATFORM_LABELS[target.platform]}
                    </span>
                    <span className="block truncate text-xs text-muted">
                      {formatAccountLine(target.name, target.externalTargetId)}
                    </span>
                  </span>
                </span>
                <span
                  className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded border transition',
                    selected ? 'border-brand bg-brand text-white' : 'border-line',
                  )}
                >
                  {selected ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : null}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {warnedSelection.length > 0 ? (
        <div className="rounded-brand border border-warning-line bg-warning-surface p-4 text-sm text-warning">
          {warnedSelection.map((layout) => layout.name).join(', ')}{' '}
          {warnedSelection.length === 1 ? 'arrives' : 'arrive'} at the channel as a single still
          image. Distributing anyway is fine — the rest of the selection is unaffected.
        </div>
      ) : null}

      {createDistribution.error ? <ErrorNotice error={createDistribution.error} /> : null}

      <Card className="flex items-center justify-between p-4">
        <p className="text-sm text-body">
          {selectedLayouts.length} layout{selectedLayouts.length === 1 ? '' : 's'} to{' '}
          {selectedTargets.length} target{selectedTargets.length === 1 ? '' : 's'}
        </p>
        <Button disabled={!canSubmit} onClick={submit}>
          {createDistribution.isPending ? 'Submitting…' : 'Distribute'}
        </Button>
      </Card>

      {previous.data && previous.data.distributions.length > 0 ? (
        <section>
          <SectionHeading
            title="Previously sent"
            description="Earlier runs for this project, so the same creatives are not sent twice by mistake."
          />
          <DistributionList
            distributions={previous.data.distributions}
            targets={targets.data?.targets ?? []}
            linkBase="/experience/distributions"
            showSource={false}
          />
        </section>
      ) : null}
    </div>
  );
}

/**
 * One selectable creative, shaped after the host's layout picker: framed
 * preview, checkbox in the corner, then name and dimensions.
 *
 * Sized to sit six to a row, so the grid stays scannable on projects with many
 * sizes. Only `Animated` earns a pill — now that print is filtered out
 * upstream, a `Digital` pill on every card would say nothing.
 */
function LayoutCard({
  layout,
  state,
  reason,
  selected,
  onToggle,
}: {
  layout: SourceLayout;
  state: LayoutDistributability;
  reason: string | null;
  selected: boolean;
  onToggle: () => void;
}) {
  const blocked = state.status === 'blocked';

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      aria-label={layout.name}
      disabled={blocked}
      onClick={onToggle}
      className={cn(
        'flex flex-col overflow-hidden rounded-brand border bg-surface text-left transition',
        blocked && 'cursor-not-allowed border-line-subtle opacity-60',
        !blocked && selected && 'border-brand ring-1 ring-brand',
        !blocked && !selected && 'border-line hover:bg-hover',
      )}
    >
      <div className="relative aspect-square bg-subtle">
        {layout.previewUrl ? (
          <img src={layout.previewUrl} alt="" className="h-full w-full object-contain p-2" />
        ) : (
          <div className="flex h-full items-center justify-center text-[11px] text-muted">
            Rendering…
          </div>
        )}
        {blocked ? null : (
          <span
            className={cn(
              'absolute left-2 top-2 flex h-4 w-4 items-center justify-center rounded border transition',
              selected ? 'border-brand bg-brand text-white' : 'border-line bg-surface',
            )}
          >
            {selected ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
          </span>
        )}
      </div>

      <div className="space-y-1 p-2">
        <p className="truncate text-xs font-medium" title={layout.name}>
          {layout.name}
        </p>
        <p className="text-[11px] text-muted">
          {formatDimensions(layout.width, layout.height, layout.unitOfMeasurement)}
        </p>

        {layout.outputIntent === 'digital_animated' ? (
          <span className="inline-flex rounded-full bg-warning-bg px-2 py-0.5 text-[11px] font-medium text-warning">
            Animated
          </span>
        ) : null}

        {reason ? (
          <p
            className={cn(
              'rounded px-1.5 py-1 text-[11px]',
              blocked ? 'bg-subtle text-muted' : 'bg-warning-surface text-warning',
            )}
          >
            {reason}
          </p>
        ) : null}
      </div>
    </button>
  );
}
